import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { cn } from "@/lib/utils";
import betaBorder from "@/assets/Borders/beta.png";

interface UserAvatarProps {
    src?: string;
    fallback?: string;
    className?: string;
    borderStyle?: string;
    size?: "sm" | "md" | "lg" | "xl" | "xxl";
    onClick?: () => void;
}

export const UserAvatar = ({
    src,
    fallback = "U",
    className,
    borderStyle,
    size = "md",
    onClick
}: UserAvatarProps) => {
    const sizeClasses = {
        sm: "h-8 w-8",
        md: "h-10 w-10",
        lg: "h-16 w-16",
        xl: "h-24 w-24",
        xxl: "h-32 w-32",
    };

    const borderStyles: Record<string, string> = {
        neon: "ring-2 ring-primary shadow-[0_0_15px_hsl(var(--primary)/0.5)]",
        gold: "ring-2 ring-yellow-500 shadow-[0_0_15px_hsl(45_100%_50%/0.5)]",
        fluo: "ring-2 ring-pink-500 shadow-[0_0_15px_rgba(236,72,153,0.5)]",
        green: "ring-2 ring-green-500 shadow-[0_0_15px_rgba(34,197,94,0.5)]",
        purple: "p-[2px] bg-gradient-to-r from-purple-500 to-blue-500 shadow-[0_0_15px_rgba(168,85,247,0.5)]",
        white: "ring-2 ring-white shadow-[0_0_15px_rgba(255,255,255,0.5)]",
    };

    const imageBorders: Record<string, string> = {
        beta: betaBorder,
    };

    // Check if borderStyle is a direct URL (starts with http or https)
    const isUrlBorder = borderStyle?.startsWith('http') || borderStyle?.startsWith('https');

    return (
        <div
            className={cn(
                "relative flex items-center justify-center transition-transform rounded-full",
                onClick && "cursor-pointer hover:scale-105",
                sizeClasses[size],
                borderStyle && borderStyles[borderStyle],
                className
            )}
            onClick={onClick}
        >
            {/* Global Pulse Effect for Special Borders */}
            {(borderStyle === 'neon' || borderStyle === 'player_border_neon' || (isUrlBorder && borderStyle?.toLowerCase().includes('neon'))) && (
                <div className="absolute inset-0 bg-cyan-500/50 rounded-full blur-xl animate-pulse scale-110" />
            )}
            {(borderStyle === 'gold' || borderStyle === 'player_border_gold' || (isUrlBorder && borderStyle?.toLowerCase().includes('gold'))) && (
                <div className="absolute inset-0 bg-yellow-500/50 rounded-full blur-xl animate-pulse scale-110" />
            )}
            {/* New Colors */}
            {(borderStyle === 'fluo' || borderStyle?.toLowerCase().includes('fluo')) && (
                <div className="absolute inset-0 bg-pink-500/50 rounded-full blur-xl animate-pulse scale-110" />
            )}
            {(borderStyle === 'green' || borderStyle?.toLowerCase().includes('green')) && (
                <div className="absolute inset-0 bg-green-500/50 rounded-full blur-xl animate-pulse scale-110" />
            )}
            {(borderStyle === 'purple' || borderStyle?.toLowerCase().includes('purple')) && (
                <div className="absolute inset-0 bg-gradient-to-r from-purple-500 to-blue-500 opacity-60 rounded-full blur-xl animate-pulse scale-110" />
            )}
            {(borderStyle === 'white' || borderStyle?.toLowerCase().includes('white')) && (
                <div className="absolute inset-0 bg-white/60 rounded-full blur-xl animate-pulse scale-110" />
            )}

            <Avatar className={cn("h-full w-full relative z-10 rounded-full")}>
                <AvatarImage
                    src={src || undefined}
                    alt={fallback}
                    className="object-cover"
                />
                <AvatarFallback className="bg-slate-700 text-slate-200">
                    {fallback}
                </AvatarFallback>
            </Avatar>
            {
                borderStyle && (
                    (borderStyle.startsWith('http') || borderStyle.startsWith('/') || borderStyle.startsWith('https')) ? (
                        <img
                            src={borderStyle}
                            className="absolute inset-[0%] w-[100%] h-[100%] max-w-none pointer-events-none object-contain scale-110 z-20"
                            alt="Border"
                        />
                    ) : imageBorders[borderStyle] && (
                        <img
                            src={imageBorders[borderStyle]}
                            className="absolute inset-[0%] w-[100%] h-[100%] max-w-none pointer-events-none object-contain z-20"
                            alt="Border"
                        />
                    )
                )
            }
        </div >
    );
};
